// components/DeviceSelector.js
import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  onSnapshot
} from 'firebase/firestore'
import { app } from '../firebase'
import { useAuth } from '../contexts/AuthContext'
import UserService from '../services/UserService'
import './DeviceSelector.css'

function DeviceSelector ({
  value,
  onChange,
  disabled,
  compact = false,
  showLabel = true
}) {
  const { user } = useAuth()
  const [devices, setDevices] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const searchInputRef = useRef(null)

  const db = getFirestore(app)

  useEffect(() => {
    if (!user) {
      setLoading(false)
      setError('Bitte melden Sie sich an')
      return
    }

    let unsubscribe = () => {}

    const setupListener = async () => {
      try {
        // Get user's companyId

        const companyId = await UserService.getUserCompanyId(user.uid)

        // Real-time listener für company-scoped devices
        const devicesRef = collection(db, 'companies', companyId, 'devices')
        const q = query(
          devicesRef,
          where('status', '==', 'active'), // Nur aktive Geräte
          orderBy('displayName', 'asc') // Alphabetisch sortiert
        )

        unsubscribe = onSnapshot(
          q,
          snapshot => {
            try {
              const devicesData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
              }))

              setDevices(devicesData)
              setError(null)
            } catch (err) {
              console.error('Error processing devices:', err)
              setError('Fehler beim Verarbeiten der Geräte')
            } finally {
              setLoading(false)
            }
          },
          err => {
            console.error('Fehler beim Laden der Geräte:', err)
            setError('Geräteliste konnte nicht geladen werden')
            setLoading(false)
          }
        )
      } catch (err) {
        console.error('Error setting up devices listener:', err)
        setError('Fehler beim Initialisieren')
        setLoading(false)
      }
    }

    setupListener()

    return () => unsubscribe()
  }, [db, user])

  // Fokus auf Suchfeld wenn Modal öffnet und Body-Scroll verhindern
  useEffect(() => {
    if (isModalOpen) {
      // Verhindere Body-Scroll wenn Modal offen ist
      const originalStyle = window.getComputedStyle(document.body).overflow
      document.body.style.overflow = 'hidden'
      
      // Fokus auf Suchfeld
      if (searchInputRef.current) {
        setTimeout(() => {
          searchInputRef.current?.focus()
        }, 100)
      }

      return () => {
        document.body.style.overflow = originalStyle
      }
    }
  }, [isModalOpen])

  // Filtere Geräte basierend auf Suchanfrage
  const filteredDevices = devices.filter(device => {
    if (!searchQuery.trim()) return true
    const query = searchQuery.toLowerCase()
    const displayName = device.customDisplayName || device.displayName
    return (
      displayName?.toLowerCase().includes(query) ||
      device.displayName?.toLowerCase().includes(query) ||
      device.manufacturer?.toLowerCase().includes(query)
    )
  })

  // Gruppiere und sortiere Geräte nach Hersteller
  const groupedDevices = filteredDevices.reduce((groups, device) => {
    const manufacturer = device.manufacturer || 'Ohne Hersteller'
    if (!groups[manufacturer]) {
      groups[manufacturer] = []
    }
    groups[manufacturer].push(device)
    return groups
  }, {})

  // Sortiere Hersteller alphabetisch und Geräte innerhalb jeder Gruppe
  const sortedGroups = Object.keys(groupedDevices)
    .sort((a, b) => {
      // "Ohne Hersteller" ans Ende
      if (a === 'Ohne Hersteller') return 1
      if (b === 'Ohne Hersteller') return -1
      return a.localeCompare(b, 'de')
    })
    .map(manufacturer => ({
      manufacturer,
      devices: groupedDevices[manufacturer].sort((a, b) => {
        const nameA = (a.customDisplayName || a.displayName || '').toLowerCase()
        const nameB = (b.customDisplayName || b.displayName || '').toLowerCase()
        return nameA.localeCompare(nameB, 'de')
      })
    }))

  const handleDeviceSelect = (deviceName) => {
    onChange(deviceName)
    setIsModalOpen(false)
    setSearchQuery('')
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    setSearchQuery('')
  }

  // Keyboard-Navigation im Modal
  const handleKeyDown = (e, deviceName) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleDeviceSelect(deviceName)
    }
  }

  // ESC zum Schließen des Modals
  useEffect(() => {
    if (!isModalOpen) return
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsModalOpen(false)
        setSearchQuery('')
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isModalOpen])

  const wrapperClasses = `
    device-selector-wrapper
    ${compact ? 'device-selector-compact' : ''}
    ${loading ? 'loading' : ''}
    ${error ? 'error' : ''}
    ${disabled ? 'disabled' : ''}
  `

  // Diese Funktion ermittelt den anzuzeigenden Text
  const getDisplayText = () => {
    if (loading) return 'Lade Geräte...'
    if (error) return 'Fehler beim Laden'
    if (!value) return 'Gerät auswählen...'

    // Finde das Gerät, das dem 'value' entspricht
    const selectedDevice = devices.find(device => device.displayName === value)
    return selectedDevice
      ? selectedDevice.customDisplayName || selectedDevice.displayName
      : 'Gerät auswählen...'
  }

  // Bestimmt, ob der Text als Platzhalter gestylt werden soll
  const isPlaceholder = loading || error || !value

  return (
    <>
      <div className={wrapperClasses}>
        {showLabel && <label className='device-selector-label'>Gerät</label>}

        <div 
          className='device-selector-container'
          onClick={() => !disabled && !loading && !error && setIsModalOpen(true)}
          role="button"
          tabIndex={disabled || loading || error ? -1 : 0}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !disabled && !loading && !error) {
              e.preventDefault()
              setIsModalOpen(true)
            }
          }}
        >
          <div className='device-icon'>
            <svg
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
            >
              <rect x='2' y='3' width='20' height='14' rx='2' ry='2' />
              <line x1='8' y1='21' x2='16' y2='21' />
              <line x1='12' y1='17' x2='12' y2='21' />
            </svg>
          </div>

          <div
            className={`device-selector-display-text ${
              isPlaceholder ? 'placeholder' : ''
            }`}
          >
            {getDisplayText()}
          </div>

          <div className='device-selector-arrow'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='12'
              height='12'
              viewBox='0 0 12 12'
            >
              <path
                fill='currentColor'
                d='M10.293 3.293L6 7.586 1.707 3.293A1 1 0 00.293 4.707l5 5a1 1 0 001.414 0l5-5a1 1 0 10-1.414-1.414z'
              />
            </svg>
          </div>
        </div>

        {error && (
          <div className='device-selector-error-message'>
            <svg
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              style={{ width: '16px', height: '16px' }}
            >
              <circle cx='12' cy='12' r='10' />
              <line x1='12' y1='8' x2='12' y2='12' />
              <line x1='12' y1='16' x2='12.01' y2='16' />
            </svg>
            {error}
          </div>
        )}
      </div>

      {/* Modal für Geräteauswahl - gerendert als Portal direkt im body */}
      {isModalOpen &&
        createPortal(
          <div 
            className='device-selector-modal-overlay'
            onClick={handleModalClose}
          >
            <div 
              className='device-selector-modal-content'
              onClick={e => e.stopPropagation()}
            >
              <div className='device-selector-modal-header'>
                <h3>Gerät auswählen</h3>
                <button 
                  className='device-selector-modal-close'
                  onClick={handleModalClose}
                  aria-label="Schließen"
                >
                  <svg
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                  >
                    <line x1='18' y1='6' x2='6' y2='18' />
                    <line x1='6' y1='6' x2='18' y2='18' />
                  </svg>
                </button>
              </div>

              <div className='device-selector-modal-search'>
                <svg
                  className='device-selector-search-icon'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                >
                  <circle cx='11' cy='11' r='8' />
                  <path d='m21 21-4.35-4.35' />
                </svg>
                <input
                  ref={searchInputRef}
                  type='text'
                  placeholder='Gerät suchen...'
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className='device-selector-search-input'
                />
                {searchQuery && (
                  <button
                    className='device-selector-search-clear'
                    onClick={() => setSearchQuery('')}
                    aria-label="Suche löschen"
                  >
                    <svg
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2'
                    >
                      <line x1='18' y1='6' x2='6' y2='18' />
                      <line x1='6' y1='6' x2='18' y2='18' />
                    </svg>
                  </button>
                )}
              </div>

              <div className='device-selector-modal-list'>
                {loading ? (
                  <div className='device-selector-modal-loading'>
                    Lade Geräte...
                  </div>
                ) : filteredDevices.length === 0 ? (
                  <div className='device-selector-modal-empty'>
                    {searchQuery ? (
                      <>Keine Geräte gefunden für "{searchQuery}"</>
                    ) : (
                      <>Keine Geräte verfügbar</>
                    )}
                  </div>
                ) : (
                  sortedGroups.map(group => (
                    <div key={group.manufacturer} className='device-selector-modal-group'>
                      <div className='device-selector-modal-group-header'>
                        {group.manufacturer}
                        <span className='device-selector-modal-group-count'>
                          ({group.devices.length})
                        </span>
                      </div>
                      {group.devices.map(device => (
                        <div
                          key={device.id}
                          className={`device-selector-modal-item ${
                            value === device.displayName ? 'selected' : ''
                          }`}
                          onClick={() => handleDeviceSelect(device.displayName)}
                          onKeyDown={e => handleKeyDown(e, device.displayName)}
                          role="button"
                          tabIndex={0}
                        >
                          <div className='device-selector-modal-item-icon'>
                            <svg
                              viewBox='0 0 24 24'
                              fill='none'
                              stroke='currentColor'
                              strokeWidth='2'
                            >
                              <rect x='2' y='3' width='20' height='14' rx='2' ry='2' />
                              <line x1='8' y1='21' x2='16' y2='21' />
                              <line x1='12' y1='17' x2='12' y2='21' />
                            </svg>
                          </div>
                          <div className='device-selector-modal-item-info'>
                            <div className='device-selector-modal-item-name'>
                              {device.customDisplayName || device.displayName}
                            </div>
                          </div>
                          {value === device.displayName && (
                            <div className='device-selector-modal-item-check'>
                              <svg
                                viewBox='0 0 24 24'
                                fill='none'
                                stroke='currentColor'
                                strokeWidth='2'
                              >
                                <polyline points='20 6 9 17 4 12' />
                              </svg>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ))
                )}
              </div>

              {filteredDevices.length > 0 && (
                <div className='device-selector-modal-footer'>
                  {filteredDevices.length} {filteredDevices.length === 1 ? 'Gerät' : 'Geräte'} 
                  {searchQuery && ` gefunden`}
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  )
}

export default DeviceSelector
